import React, { useEffect, useState } from "react";
import { Gift, Mail, BookOpen, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { log } from "../lib/log";

// Welcome PDFs (8-Day Healing Blueprint + Destination Wellness Ch.1) — emailed once after the birth chart; re-send on demand.
export default function WelcomeGifts() {
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/users/welcome-gifts/status").then((r) => { if (alive) setStatus(r.data); }).catch((e) => log.warn("gifts status failed:", e));
    return () => { alive = false; };
  }, []);

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/users/welcome-gifts/resend");
      toast.success("Your healing gifts are on their way — check your inbox");
      setStatus((s) => ({ ...s, sent_at: new Date().toISOString() }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send right now");
    } finally {
      setSending(false);
    }
  };

  if (!status) return null;

  return (
    <section className="mt-10" data-testid="welcome-gifts">
      <div className="glass-gold rounded-2xl p-5 sm:p-6 flex flex-col md:flex-row md:items-center gap-5">
        <div className="w-12 h-12 rounded-full bg-[#C8A96E]/15 border border-[#C8A96E]/40 flex items-center justify-center shrink-0">
          <Gift size={20} className="text-[#C8A96E]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C8A96E]">Your healing gifts</div>
          <div className="font-heading text-xl sm:text-2xl mt-1 text-white">Two free PDFs, sent to your inbox</div>
          <ul className="mt-2 space-y-1 text-sm text-[#E8DFD3]">
            {status.gifts.map((g) => (
              <li key={g.file} className="flex items-start gap-2" data-testid={`gift-${g.file}`}>
                <BookOpen size={13} className="text-[#C8A96E] mt-1 shrink-0" />
                <span>{g.title}</span>
              </li>
            ))}
          </ul>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A89C98] mt-2" data-testid="welcome-gifts-status">
            {status.sent_at ? `Emailed ${new Date(status.sent_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : status.configured ? "Sending shortly" : "Email delivery not configured yet"}
          </div>
          {status.book && (
            <a
              href={status.book.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="welcome-gifts-book-link"
              className="mt-3 inline-flex items-center gap-2 text-xs text-[#C8A96E] hover:text-white transition-colors"
            >
              <ShoppingBag size={13} />
              <span>Get the full book · <em className="not-italic font-heading">{status.book.title}</em> by {status.book.author} →</span>
            </a>
          )}
        </div>
        <button
          onClick={resend}
          disabled={sending || !status.configured}
          data-testid="welcome-gifts-resend-btn"
          className="inline-flex items-center justify-center gap-2 h-10 px-5 uppercase tracking-widest text-[11px] font-bold border border-[#C8A96E] text-[#C8A96E] hover:bg-[#C8A96E] hover:text-[#15111A] disabled:opacity-40 transition-colors shrink-0"
        >
          <Mail size={14} /> {sending ? "Sending…" : "Email me again"}
        </button>
      </div>
    </section>
  );
}
